import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPaperPlane, faTrash, faRobot, faClipboardList, faPen, faCheck, faComments, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS } from '../utils/colors';
import { useWarehouseContext } from '../context/WarehouseContext';
import aiAdvisorService from '../services/aiAdvisorService';
import MessageAlert from '../components/common/MessageAlert';
import SearchableSelect from '../components/common/SearchableSelect';

/**
 * AI Chat — chat-style advisor over the business's own anonymised sales/purchase
 * trends. Per-user threads (sessions) on the left, transcript + composer on the
 * right. Advisory only; the "Generate plan" button records a plan turn in the
 * same thread.
 */
const AIChatPage = () => {
  const { t } = useTranslation();
  const strings = t('aiAdvisor', { returnObjects: true });
  const { selectedWarehouseIds } = useWarehouseContext();

  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [windowWeeks, setWindowWeeks] = useState(8);
  const [categories, setCategories] = useState([]); // Sub Categories for the scope picker
  const [catalogueIds, setCatalogueIds] = useState([]); // selected Sub Category ids; [] = all
  const [renamingId, setRenamingId] = useState(null); // thread id being renamed
  const [renameText, setRenameText] = useState('');
  const [threadDrawerOpen, setThreadDrawerOpen] = useState(false); // mobile thread drawer

  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [disabled, setDisabled] = useState(false); // feature off for tenant (503)
  const [alert, setAlert] = useState(null);

  const transcriptRef = useRef(null);

  const showError = useCallback((res) => {
    if (res?.status === 503) {
      setDisabled(true);
      return;
    }
    setAlert({ type: 'error', title: t('commonCols.aiAdvisor'), message: res?.error || strings.errors.generic });
  }, []);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await aiAdvisorService.listSessions({ limit: 50, offset: 0 });
      if (!res.success) return showError(res);
      setDisabled(false);
      const list = res.data?.sessions || [];
      setSessions(list);
      // Auto-open the most recent thread if none active
      if (list.length && activeId == null) setActiveId(list[0].id);
    } catch (err) {
      showError({ error: err.message });
    } finally {
      setLoadingSessions(false);
    }
  }, [activeId, showError]);

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Sub Categories for the scope picker (non-fatal if it fails)
  useEffect(() => {
    let active = true;
    (async () => {
      const res = await aiAdvisorService.getCategories();
      if (active && res.success) setCategories(res.data?.categories || []);
    })();
    return () => { active = false; };
  }, []);

  // Load the active thread's messages
  useEffect(() => {
    if (activeId == null) {
      setMessages([]);
      return;
    }
    let active = true;
    (async () => {
      setLoadingThread(true);
      try {
        const res = await aiAdvisorService.getSession(activeId);
        if (!active) return;
        if (!res.success) {
          if (res.status === 404) {
            // thread gone — drop it and pick another
            setSessions((prev) => prev.filter((s) => s.id !== activeId));
            setActiveId(null);
            return;
          }
          return showError(res);
        }
        setMessages(res.data?.messages || []);
      } catch (err) {
        if (active) showError({ error: err.message });
      } finally {
        if (active) setLoadingThread(false);
      }
    })();
    return () => { active = false; };
  }, [activeId, showError]);

  // Keep transcript scrolled to bottom
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleNewChat = async () => {
    const res = await aiAdvisorService.createSession();
    if (!res.success) return showError(res);
    const session = res.data;
    setSessions((prev) => [session, ...prev]);
    setActiveId(session.id);
    setMessages([]);
    setThreadDrawerOpen(false);
  };

  // Pick a thread and (on mobile) close the drawer to reveal the chat
  const selectThread = (id) => {
    setActiveId(id);
    setThreadDrawerOpen(false);
  };

  const activeTitle =
    sessions.find((s) => s.id === activeId)?.title || strings.chat.newChatTitle;

  // Ensure there's a thread to post into; returns the id or null on failure.
  const ensureSession = async () => {
    if (activeId != null) return activeId;
    const res = await aiAdvisorService.createSession();
    if (!res.success) {
      showError(res);
      return null;
    }
    setSessions((prev) => [res.data, ...prev]);
    setActiveId(res.data.id);
    return res.data.id;
  };

  const handleSend = async () => {
    const q = question.trim();
    if (!q || sending) return;
    const id = await ensureSession();
    if (id == null) return;

    setSending(true);
    setQuestion('');
    // Optimistically append the user's bubble
    const optimistic = { id: `tmp-${Date.now()}`, role: 'user', content: q, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const res = await aiAdvisorService.chat(id, {
        question: q,
        window_weeks: windowWeeks,
        warehouse_ids: selectedWarehouseIds,
        catalogue_ids: catalogueIds,
      });

      if (!res.success) {
        // roll back optimistic bubble, restore the text
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setQuestion(q);
        return showError(res);
      }

      const data = res.data;
      if (data?.data_available === false) {
        // No turns recorded — drop optimistic and show the message inline
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setAlert({ type: 'info', title: t('commonCols.aiAdvisor'), message: data.answer || strings.chat.noData });
        setQuestion(q);
        return;
      }

      // Replace optimistic user bubble with the persisted pair
      setMessages((prev) => {
        const withoutTmp = prev.filter((m) => m.id !== optimistic.id);
        const turns = [];
        if (data.user_message) turns.push(data.user_message);
        if (data.assistant_message) turns.push(data.assistant_message);
        return [...withoutTmp, ...turns];
      });
      refreshTitleFrom(id);
    } catch (err) {
      // roll back optimistic bubble, restore the text
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setQuestion(q);
      showError({ error: err.message });
    } finally {
      setSending(false);
    }
  };

  const handlePlan = async () => {
    if (sending) return;
    const id = await ensureSession();
    if (id == null) return;
    setSending(true);
    try {
      const res = await aiAdvisorService.plan(id, {
        window_weeks: windowWeeks,
        warehouse_ids: selectedWarehouseIds,
        catalogue_ids: catalogueIds,
      });
      if (!res.success) return showError(res);
      const data = res.data;
      if (data?.data_available === false) {
        setAlert({ type: 'info', title: t('commonCols.aiAdvisor'), message: data.plan || strings.plan.noData });
        return;
      }
      setMessages((prev) => {
        const turns = [];
        if (data.user_message) turns.push(data.user_message);
        if (data.assistant_message) turns.push(data.assistant_message);
        return [...prev, ...turns];
      });
      refreshTitleFrom(id);
    } catch (err) {
      showError({ error: err.message });
    } finally {
      setSending(false);
    }
  };

  // The backend auto-names a thread from the first question; refresh its title.
  const refreshTitleFrom = async (id) => {
    const res = await aiAdvisorService.getSession(id);
    if (res.success && res.data) {
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title: res.data.title, updated_at: res.data.updated_at } : s)));
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(strings.chat.deleteConfirm)) return;
    const res = await aiAdvisorService.deleteSession(id);
    if (!res.success) return showError(res);
    setSessions((prev) => prev.filter((s) => s.id !== id));
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
  };

  const startRename = (e, session) => {
    e.stopPropagation();
    setRenamingId(session.id);
    setRenameText(session.title || '');
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameText('');
  };

  const commitRename = async (id) => {
    const title = renameText.trim();
    const current = sessions.find((s) => s.id === id);
    // No change or empty → just close the editor
    if (!title || title === (current?.title || '')) {
      cancelRename();
      return;
    }
    // Optimistic update; roll back on failure
    const prevTitle = current?.title;
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
    cancelRename();
    const res = await aiAdvisorService.renameSession(id, title);
    if (!res.success) {
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title: prevTitle } : s)));
      return showError(res);
    }
    if (res.data) {
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, ...res.data } : s)));
    }
  };

  if (disabled) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <FontAwesomeIcon icon={faRobot} className="text-4xl mb-3 text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{strings.chat.title}</h2>
          <p className="text-gray-600">{strings.errors.notEnabled}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
      {alert && (
        <div className="fixed top-4 right-4 z-50">
          <MessageAlert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FontAwesomeIcon icon={faRobot} /> {strings.chat.title}
        </h1>
        <p className="text-sm text-gray-500">{strings.chat.subtitle}</p>
      </div>

      <div className="relative flex gap-4 h-[75vh] lg:h-[70vh]">
        {/* Mobile backdrop when the thread drawer is open */}
        {threadDrawerOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 lg:hidden"
            onClick={() => setThreadDrawerOpen(false)}
          />
        )}

        {/* Threads sidebar — static on desktop, off-canvas drawer on mobile */}
        <div
          className={`bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden
            lg:static lg:translate-x-0 lg:w-[260px] lg:shrink-0
            fixed top-0 left-0 z-40 h-full w-[80%] max-w-[300px] transition-transform duration-200
            ${threadDrawerOpen ? 'translate-x-0' : '-translate-x-full'}`}
        >
          <div className="flex items-center justify-between px-3 pt-3 lg:hidden">
            <span className="text-sm font-semibold text-gray-700">{strings.chat.threadsHeader}</span>
            <button onClick={() => setThreadDrawerOpen(false)} className="text-gray-400 hover:text-gray-700 p-1">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <button
            onClick={handleNewChat}
            className="m-3 px-4 py-2 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2"
            style={{ backgroundColor: UI_COLORS.NAVIGATION?.ACTIVE || '#1f2937' }}
          >
            <FontAwesomeIcon icon={faPlus} /> {strings.chat.newChat}
          </button>
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {loadingSessions ? (
              <p className="text-sm text-gray-400 px-2 py-3">{t('common.buttons.loading')}</p>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-gray-400 px-2 py-3">{strings.chat.emptyThreads}</p>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.id}
                  onClick={() => renamingId !== s.id && selectThread(s.id)}
                  className={`group flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm mb-1 ${
                    activeId === s.id ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-50'
                  }`}
                >
                  {renamingId === s.id ? (
                    <>
                      <input
                        autoFocus
                        value={renameText}
                        onChange={(e) => setRenameText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(s.id); }
                          else if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                        onBlur={() => commitRename(s.id)}
                        className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded-md text-sm font-normal focus:outline-none focus:ring-1 focus:ring-gray-400"
                        maxLength={120}
                      />
                      <button
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); commitRename(s.id); }}
                        className="text-gray-400 hover:text-green-600"
                        title={t('common.buttons.save')}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="truncate flex-1">{s.title || strings.chat.newChatTitle}</span>
                      <button
                        onClick={(e) => startRename(e, s)}
                        className="p-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 text-gray-400 hover:text-gray-700"
                        title={strings.chat.rename}
                        aria-label={strings.chat.rename}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, s.id)}
                        className="p-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 text-gray-400 hover:text-red-500"
                        title={strings.chat.delete}
                        aria-label={strings.chat.delete}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Transcript + composer */}
        <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col overflow-hidden">
          {/* Mobile bar: open thread drawer + show active title */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 lg:hidden">
            <button
              onClick={() => setThreadDrawerOpen(true)}
              className="text-gray-600 hover:text-gray-900 p-1"
              aria-label={strings.chat.threadsHeader}
            >
              <FontAwesomeIcon icon={faComments} />
            </button>
            <span className="text-sm font-semibold text-gray-700 truncate">{activeTitle}</span>
          </div>
          <div ref={transcriptRef} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
            {loadingThread ? (
              <p className="text-sm text-gray-400">{t('common.buttons.loading')}</p>
            ) : messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-center px-6">
                <p className="text-gray-400 text-sm">{strings.chat.emptyMessages}</p>
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[88%] sm:max-w-[80%] px-3 sm:px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                      m.role === 'user' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-2 rounded-2xl text-sm bg-gray-100 text-gray-500 italic">
                  {strings.chat.thinking}
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-gray-100 p-3">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500 whitespace-nowrap">{strings.chat.windowWeeks}:</label>
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={windowWeeks}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setWindowWeeks(Number.isNaN(v) ? 8 : Math.min(52, Math.max(1, v)));
                  }}
                  className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              {categories.length > 0 && (
                <div className="flex-1 basis-[160px] min-w-0 max-w-[260px]">
                  <SearchableSelect
                    name="catalogue_ids"
                    isMulti
                    value={catalogueIds}
                    onChange={(e) => setCatalogueIds(e.target.value || [])}
                    options={categories.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder={strings.chat.subCategoryPlaceholder}
                  />
                </div>
              )}
              <button
                onClick={handlePlan}
                disabled={sending}
                className="ml-auto px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
                title={strings.chat.generatePlan}
              >
                <FontAwesomeIcon icon={faClipboardList} />
                <span className="hidden sm:inline">{strings.chat.generatePlan}</span>
              </button>
            </div>
            <div className="flex items-end gap-2">
              <textarea
                rows={2}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={strings.chat.inputPlaceholder}
                className="flex-1 min-w-0 resize-none px-3 py-2 border border-gray-200 rounded-xl text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
              <button
                onClick={handleSend}
                disabled={sending || !question.trim()}
                className="shrink-0 px-3 sm:px-4 py-2 rounded-xl text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: UI_COLORS.NAVIGATION?.ACTIVE || '#1f2937' }}
                aria-label={strings.chat.send}
              >
                <FontAwesomeIcon icon={faPaperPlane} /> <span className="hidden sm:inline">{strings.chat.send}</span>
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">{strings.chat.privacyNote}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;
